import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFormatter, type ChangeItem, type ChangeValue, type IsoInstant } from '@sl/core';
import {
  stagedMarkersFor,
  withStagedMarkers,
  type StagedMarker,
  type StagedMarkers,
  type StagedRow,
  type StagedRowItem,
} from '@sl/core/rows';
import { useShell } from '../shell/shellContext';
import { useAnnounce } from '../live/LiveRegion';
import { STAGED_COPY } from './copy';

// THE OPEN CHANGE-SET, READ ONCE (T-129; ARCH §9.3, REQ-101 AC1 / AC4).
//
// **One read for the whole app.** `queries.changeSet(accountId)` is a single query key, so the shell reads
// it here and every screen below takes the answer out of this context instead of asking again. React Query
// would dedupe a second `useQuery` on the same key anyway; the context exists so that nothing has to
// remember to pass the same options, and so that "the tray is server state, read at the shell" is a fact
// about the code rather than a convention.
//
// **Staging does not refetch a list, and that is the point.** `invalidation.stageChange` names `changes`,
// `accountInfo` and `spendPacing` and deliberately NO entity family (the tech-lead's T-125 note (3)): a
// staged edit has not happened, so the rows' own numbers have not moved, and refetching thirty rows to draw
// a marker the client already holds would make the screen flicker for nothing. The marker is MERGED onto
// the rows that are already on screen, by `withStagedMarkers`. `staged.rows.test.tsx` asserts the entities
// query is not invalidated when something is staged.
//
// **The markers never rewrite the row's own values.** An ad set staged off is still running and still
// spending until someone publishes; `row.staged` carries both values and the item id so the renderer can
// draw the new one with the old one struck through (AC1) and offer to remove the right item (AC2).

export interface StagedValue {
  /** Every staged item of the open set, in tray order. Empty while it loads and when nothing is staged. */
  items: readonly ChangeItem[];
  /** REQ-101 AC4's number — what the pill says. `0` hides it. */
  count: number;
  /** REQ-101 AC5 — when the oldest one was staged, or `null` when the tray is empty. */
  oldestStagedAt: IsoInstant | null;
  /** The read is in flight. The pill and the markers draw nothing rather than a zero or a skeleton. */
  isPending: boolean;
  /** The read failed. Everything staged-shaped is simply absent — a marker is never a state to recover. */
  isError: boolean;
  /**
   * BUG-098 (w07 ②): the entity whose budget editor the tray's pencil asked to reopen, until that row takes it.
   * App-level state rather than a URL parameter, so the ask survives the tray's own navigation to the level
   * that lists the row, and a reload does not reopen an editor nobody asked for.
   */
  editRequest: string | null;
  requestEdit: (entityId: string) => void;
  clearEditRequest: () => void;
  /**
   * The markers, formatted, indexed by entity id — built ONCE per change-set, not once per row.
   *
   * **This is a performance contract, not a convenience.** The tree paints 2,000 rows and REQ-040 AC5 gives
   * it a 100 ms budget; a row that built its own formatter and re-scanned the item list would do both two
   * thousand times, and the first build of T-129 blew that budget exactly that way. A row does a `Map.get`.
   */
  textByEntity: ReadonlyMap<string, StagedRowText>;
}

const EMPTY: readonly ChangeItem[] = Object.freeze([]);
const EMPTY_TEXT: ReadonlyMap<string, StagedRowText> = new Map();

/**
 * Exported for ONE reason: the 24-hour reminder cannot be reached through the demo server. The seed's
 * `stagedAt` is a `seed` instant that `stagedAtOf` shifts into today's calendar, so the seeded tray is
 * always minutes old however far `?demoNow=` moves — see the note to @qa-engineer. A test that has to
 * supply an old `oldestStagedAt` provides it here rather than mocking a query.
 */
export const StagedContext = createContext<StagedValue | null>(null);

/**
 * Reads the open change-set for the shell's account and shares it. Mounted once, inside `<AppShell>` and
 * above the router outlet, so every screen and every overlay is below it.
 */
/** The states a change is still WAITING in — nothing here has reached Meta through this product. */
// `failed` too (T-128, D-1252): a change a publish REFUSED is still here to be retried, with its own `failure` -
// API §6.6 / §12.5 - and the demo server now keeps it as the C# host always did; Retry sends it with the rest.
const OPEN_STATES: ReadonlySet<ChangeItem['state']> = new Set(['staged', 'marked_done', 'failed']);

/** BUG-123: the ONE spelling of "an item is open" — what the tray counts, and what a revert's announcement counts. */
export const isOpenChange = (item: Pick<ChangeItem, 'state'>): boolean =>
  OPEN_STATES.has(item.state);

export function StagedProvider({ children }: { children: ReactNode }) {
  const { account, queries, locale, deviceTimeZone } = useShell();
  const query = useQuery(queries.changeSet(account.id));
  const set = query.data?.data ?? null;
  // One formatter for the whole tray, in the ACCOUNT's currency and the reader's locale.
  const fmt = useMemo(
    () =>
      createFormatter({
        account: { currency: account.currency, timeZone: account.timezone },
        locale,
        deviceTimeZone,
      }),
    [account.currency, account.timezone, locale, deviceTimeZone],
  );

  const [editRequest, setEditRequest] = useState<string | null>(null);
  const requestEdit = useCallback((entityId: string) => setEditRequest(entityId), []);
  const clearEditRequest = useCallback(() => setEditRequest(null), []);
  const value = useMemo<StagedValue>(() => {
    /**
     * **What is still waiting to happen** — and that is TWO states, not one (T-135).
     *
     * `staged` is the obvious one. `marked_done` is a change the owner has applied BY HAND in Ads Manager
     * on a View-only account (REQ-104 AC5): it is not published, it is not discarded, and the product has
     * not yet looked for itself — the row says "Not yet seen on Meta" until a refresh either verifies it
     * (and it leaves, as `verified`) or finds nothing and it stays. Filtering on `staged` alone made the
     * row VANISH the moment it was marked, which on a review screen reads exactly like "it published".
     * Found by walking Priya 6.
     */
    const items = set?.items.filter((item) => OPEN_STATES.has(item.state)) ?? EMPTY;
    return {
      items,
      // **The count is the change-set's own length, not `AccountInfo.stagedChanges`.** Both are true and
      // they are derived from the same store (T-123), but only one of them moves the instant an item is
      // staged: `invalidation.stageChange` names `accountInfo` too, so the session figure catches up a
      // round-trip later. A pill that lags the tray by one action is a pill nobody trusts, and the whole
      // point of reading the set at the shell is that the number and the markers come from one answer.
      count: items.length,
      oldestStagedAt: set?.oldestStagedAt ?? null,
      isPending: query.isPending,
      isError: query.isError,
      textByEntity: textByEntity(items, fmt),
      editRequest,
      requestEdit,
      clearEditRequest,
    };
  }, [set, query.isPending, query.isError, fmt, editRequest, requestEdit, clearEditRequest]);

  return <StagedContext.Provider value={value}>{children}</StagedContext.Provider>;
}

/**
 * The open change-set, as the shell read it. Safe above the provider: a screen rendered outside the shell
 * (a story test, the kitchen sink) gets an empty tray rather than an exception, because a missing marker is
 * the absence of a decoration and never a broken screen.
 */
export function useStaged(): StagedValue {
  return (
    useContext(StagedContext) ?? {
      items: EMPTY,
      count: 0,
      editRequest: null,
      requestEdit: () => {},
      clearEditRequest: () => {},
      oldestStagedAt: null,
      isPending: false,
      isError: false,
      textByEntity: EMPTY_TEXT,
    }
  );
}

/**
 * The list, with the open change-set merged onto it (`@sl/core/rows`). Identity-preserving on both sides:
 * a row with nothing staged comes back as the same object and an empty tray returns the same array, so a
 * memoised child below this does not re-render because the tray was read.
 */
export function useStagedRows<T extends StagedRowItem>(
  rows: readonly T[],
): readonly StagedRow<T>[] {
  const { items } = useStaged();
  return useMemo(() => withStagedMarkers(rows, items), [rows, items]);
}

/** The markers for ONE entity — a detail header, a quick-action sheet. `null` when nothing is staged. */
export function useStagedMarkers(entityId: string | null | undefined): StagedMarkers | null {
  const { items } = useStaged();
  return useMemo(
    () => (entityId ? stagedMarkersFor({ entity: { id: entityId } }, items) : null),
    [entityId, items],
  );
}

/** A staged value as a plain string, for an accessible name or an announcement. */
export function stagedValueText(
  value: ChangeValue,
  fmt: (value: number, context: 'money.perDay') => string,
  words: { on: string; off: string },
): string {
  return value.kind === 'status'
    ? value.isOn
      ? words.on
      : words.off
    : fmt(value.amount, 'money.perDay');
}

// ---- the marker as WORDS -----------------------------------------------------------------------------------

/** One staged marker with both of its values already formatted for the surface that draws it. */
export interface StagedText {
  marker: StagedMarker;
  /** The live value, struck through beside the staged one. */
  from: string;
  /** What it will be when it is published. */
  to: string;
}

export interface StagedRowText {
  /** A staged turn-on / turn-off — the switch and the status cell. */
  status: StagedText | null;
  /** A staged `daily_budget` or `lifetime_budget` — the budget cell. */
  budget: StagedText | null;
}

/**
 * The markers for one entity, formatted in the ACCOUNT's currency and the reader's locale.
 *
 * A row model arrives already formatted (`ListRowModel` is strings), so a row renderer has no formatter of
 * its own — and a staged value must be written the same way the value it replaces is, or the struck-through
 * pair reads as two different kinds of thing. The provider decides it once for the whole tray; **this is a
 * `Map.get`**, because the tree calls it two thousand times inside a 100 ms paint budget.
 */
export function useStagedText(entityId: string | null | undefined): StagedRowText | null {
  const { textByEntity } = useStaged();
  return entityId ? (textByEntity.get(entityId) ?? null) : null;
}

/** Every staged item, formatted and grouped by entity. Built once per change-set by the provider. */
function textByEntity(
  items: readonly ChangeItem[],
  fmt: ReturnType<typeof createFormatter>,
): ReadonlyMap<string, StagedRowText> {
  if (items.length === 0) return EMPTY_TEXT;
  const say = (value: ChangeValue, field: string): string =>
    value.kind === 'status'
      ? value.isOn
        ? STAGED_COPY.statusOn
        : STAGED_COPY.statusOff
      : fmt(value.amount, field === 'daily_budget' ? 'money.perDay' : 'money.headline');
  const out = new Map<string, StagedRowText>();
  for (const item of items) {
    const marker: StagedMarker = {
      itemId: item.id,
      field: item.field,
      from: item.from,
      to: item.to,
    };
    const text: StagedText = {
      marker,
      from: say(item.from, item.field),
      to: say(item.to, item.field),
    };
    const seen = out.get(item.entity.id) ?? { status: null, budget: null };
    // Last one wins per slot, the same rule `withStagedMarkers` follows: API §6.3 upserts on
    // `(entityId, field)`, so only a daily and a lifetime edit can ever land in the budget slot together.
    if (item.field === 'status') seen.status = text;
    else seen.budget = text;
    out.set(item.entity.id, seen);
  }
  return out;
}

// ---- saying it out loud (REQ-281 AC4) ----------------------------------------------------------------------

/**
 * What a staging says to a screen reader, for one outcome of `optimisticStage` (`@sl/core/queries`).
 *
 * **The count travels with the sentence** because the pill it moves may be off-screen — a reader who has
 * just staged something is looking at a row, not at the top bar. **Nothing here invents a consequence:**
 * the server's "Turns off 3 ad sets and 7 ads · about $49 per day stops spending" arrives on the item
 * 120 ms later, and a plausible sentence corrected under the reader is worse than a short one now (the
 * same rule `optimisticStage` follows by never fabricating a `ChangeItem`).
 *
 * `not_staged` says nothing at all: setting a value back to the one that is live changes nothing, so there
 * is nothing to announce.
 */
export function stagingAnnouncement(
  outcome: 'added' | 'merged' | 'removed' | 'not_staged',
  subject: string,
  count: number,
): string | null {
  if (outcome === 'not_staged') return null;
  const waiting = STAGED_COPY.waiting(String(count), count === 1);
  return outcome === 'removed'
    ? STAGED_COPY.announceRemoved(subject, waiting)
    : outcome === 'merged'
      ? STAGED_COPY.announceMerged(subject, waiting)
      : STAGED_COPY.announceAdded(subject, waiting);
}

/**
 * "Cart Abandoners 14d, budget $30" · "Clearance – Lookalike 3%, turned off" — an announcement's subject.
 *
 * `consequence` is the SERVER's own sentence and is appended when there is one (T-131's switch stages a
 * low-stakes flip without a confirm, so the announcement is the only place the reader is told what it does
 * to the money). Nothing is written here: `validateChange` has already answered by then.
 */
export function stagingSubject(
  name: string,
  to: ChangeValue,
  amount: string | null,
  consequence?: string | null,
): string {
  const what =
    to.kind === 'status'
      ? to.isOn
        ? STAGED_COPY.announceStatusOn
        : STAGED_COPY.announceStatusOff
      : STAGED_COPY.announceBudget(amount ?? '');
  const subject = STAGED_COPY.announceSubject(name, what);
  return consequence ? STAGED_COPY.announceWith(subject, consequence) : subject;
}

/**
 * The hook a staging control calls (T-130's budget editor, T-131's switch): hand it what
 * `optimisticStage` answered and it speaks through the app's ONE live region (`app/live/LiveRegion`,
 * ARCH §9.5) — polite, because a staging is routine and must not interrupt.
 */
export function useStagingAnnouncer(): (
  outcome: 'added' | 'merged' | 'removed' | 'not_staged',
  subject: string,
  count: number,
) => void {
  const say = useAnnounce();
  return useCallback(
    (outcome, subject, count) => {
      const sentence = stagingAnnouncement(outcome, subject, count);
      if (sentence !== null) say(sentence, 'polite');
    },
    [say],
  );
}
